import { LoadingButton } from '@mui/lab'
import { Alert, Button, Grid, TextField } from '@mui/material'
import React from 'react'
import { useToggle } from 'react-use'

import { formatCode } from 'components/common/Codeblock'
import useAuth from 'hooks/use-auth'
import theme from 'theme'
import { EndpointResponse } from 'types/api/end-point'
import { TeamExternalIntegration } from 'types/api/services/golden-signal'
import { testTeamExternalIntegrationEndpointConfiguration } from 'utils/team-external-integrations/team-external-integrations-util'

const TestTeamExternalIntegration: React.FC<{
  integration: TeamExternalIntegration
  authenticate: boolean
  onResponseReceived?: (endpointResponse: EndpointResponse) => void
  onError?: () => void
  disabled?: boolean
  statusCheckId?: string
}> = ({
  integration,
  onResponseReceived,
  disabled,
  onError,
  authenticate,
  statusCheckId
}) => {
  const { currentTeamId } = useAuth()
  const [isLoading, setIsLoading] = useToggle(false)
  const [responseBody, setResponseBody] = React.useState<string>()
  const [alertSeverity, setAlertSeverity] = React.useState<
    'error' | 'success'
  >()

  React.useEffect(() => {
    setAlertSeverity(null)
    setResponseBody(null)
  }, [integration?.name])

  const abortControllerRef = React.useRef<AbortController>()

  return (
    <Grid
      container
      data-test-test-status-check-integration
      direction="column"
      rowSpacing={2}
    >
      <Grid item>
        <LoadingButton
          color="primary"
          data-testid={
            authenticate
              ? 'team-integration-auth-test-auth'
              : 'team-integration-auth-test-request'
          }
          disabled={!integration.url || isLoading || disabled}
          loading={isLoading}
          size="small"
          variant="contained"
          onClick={async () => {
            setIsLoading(true)
            await testTeamExternalIntegrationEndpointConfiguration(
              integration,
              currentTeamId,
              abortControllerRef,
              (response) => {
                setIsLoading(false)
                setAlertSeverity('success')
                setResponseBody(response?.responseBody)
                onResponseReceived && onResponseReceived(response)
              },
              (responseBody) => {
                setIsLoading(false)
                setAlertSeverity('error')
                setResponseBody(responseBody)
                onError()
              },
              statusCheckId,
              authenticate
            )
          }}
        >
          Test {authenticate ? 'Authentication' : 'Request'}
        </LoadingButton>
        <Button
          color="secondary"
          data-testid="cancel-test-status-check"
          disabled={!isLoading}
          size="small"
          style={{
            display: !isLoading ? 'none' : authenticate ? 'none' : 'inline',
            marginLeft: theme.space[2]
          }}
          variant="contained"
          onClick={async () => {
            abortControllerRef.current?.abort()
            setIsLoading(false)
            setAlertSeverity(null)
            setResponseBody('Canceled')
          }}
        >
          Cancel Request
        </Button>
      </Grid>
      {!isLoading && alertSeverity && (
        <Grid item>
          <Alert
            data-testid="test-status-check-integration-alert"
            severity={alertSeverity}
          >
            {alertSeverity === 'error'
              ? 'Unable to Authenticate'
              : 'Authenticated'}
          </Alert>
        </Grid>
      )}
      {!isLoading &&
        responseBody &&
        (alertSeverity === 'error' || !authenticate) && (
          <Grid item>
            <TextField
              multiline
              contentEditable={false}
              data-testid="test-status-check-integration-response-body"
              disabled={true}
              fullWidth={true}
              id="outlined-multiline-static"
              InputProps={{ sx: { fontFamily: 'Monaco' } }}
              label="Response"
              maxRows={8}
              sx={{ fontFamily: 'Sohne Mono' }}
              value={formatCode(null, responseBody)}
            />
          </Grid>
        )}
    </Grid>
  )
}

export default TestTeamExternalIntegration
